import React, { useState, useRef } from "react";
import Icon from "./Icon";

const MultipleImageUpload = ({
    label,
    value = [],
    onChange,
    accept = "image/*",
    maxSize = 10 * 1024 * 1024, // 10MB
    className = "",
}) => {
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState("");
    const fileInputRef = useRef(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(Array.from(e.dataTransfer.files));
        }
    };

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(Array.from(e.target.files));
        }
    };

    const handleFiles = (files) => {
        setError("");
        const validFiles = [];
        const errors = [];

        files.forEach((file, index) => {
            // Validate file type
            if (!file.type.startsWith("image/")) {
                errors.push(`${file.name} is not a valid image file.`);
                return;
            }

            // Validate file size
            if (file.size > maxSize) {
                errors.push(
                    `${file.name} exceeds the maximum size of ${Math.round(
                        maxSize / 1024 / 1024
                    )}MB.`
                );
                return;
            }

            validFiles.push(file);
        });

        if (errors.length > 0) {
            setError(errors.join(" "));
        }

        if (validFiles.length > 0) {
            // Convert files to preview URLs and add to existing images
            const newImages = [];
            let processedCount = 0;

            validFiles.forEach((file) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    newImages.push({
                        file: file,
                        preview: e.target.result,
                        isNew: true,
                    });
                    processedCount++;

                    if (processedCount === validFiles.length) {
                        // Merge with existing images
                        const existingImages = Array.isArray(value) ? value : [];
                        onChange([...existingImages, ...newImages]);
                    }
                };
                reader.onerror = () => {
                    errors.push(`Failed to read ${file.name}.`);
                    processedCount++;
                    if (processedCount === validFiles.length && errors.length > 0) {
                        setError(errors.join(" "));
                    }
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const removeImage = (index) => {
        const currentImages = Array.isArray(value) ? [...value] : [];
        const imageToRemove = currentImages[index];
        
        // If it's an existing image (has path property), mark it for removal
        if (imageToRemove && imageToRemove.path && !imageToRemove.isNew) {
            // Keep it in the array but mark as removed
            imageToRemove.removed = true;
            currentImages[index] = imageToRemove;
        } else {
            // Remove new images directly
            currentImages.splice(index, 1);
        }
        
        onChange(currentImages);
    };

    const getImageUrl = (image) => {
        if (image.removed) return null;
        if (image.preview) return image.preview;
        if (image.url) return image.url;
        if (typeof image === "string") return image;
        return null;
    };

    const images = Array.isArray(value) ? value.filter(img => !img.removed) : [];

    return (
        <div className={`multiple-image-upload-container ${className}`}>
            {label && <label className="image-upload-label">{label}</label>}

            <div
                className={`image-upload-area ${
                    dragActive ? "drag-active" : ""
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={triggerFileInput}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileInput}
                    multiple
                    style={{ display: "none" }}
                />

                <div className="upload-placeholder">
                    <div className="upload-icon">
                        <Icon name="upload" size="32px" />
                    </div>
                    <p className="upload-text">Drag & drop images here</p>
                    <p className="upload-subtext">or click to browse</p>
                    <p className="upload-info">
                        Max size: {Math.round(maxSize / 1024 / 1024)}MB per image
                    </p>
                </div>
            </div>

            {images.length > 0 && (
                <div className="multiple-images-preview">
                    {images.map((image, index) => {
                        const imageUrl = getImageUrl(image);
                        if (!imageUrl) return null;

                        return (
                            <div key={index} className="image-preview-item">
                                <img src={imageUrl} alt={`Preview ${index + 1}`} />
                                <button
                                    type="button"
                                    className="remove-image-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeImage(index);
                                    }}
                                >
                                    <Icon name="x" size="16px" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {error && <div className="upload-error">{error}</div>}
        </div>
    );
};

export default MultipleImageUpload;

